import Stepper from "@/components/common/Stepper";
import { Copy } from "@/components/common/Copy";
import { getCreateUserWizardSteps, validateCreateUserWizardSearch } from "@/features/admin/utils/createUserWizard";
import { useProfileQuery, useProfileByIdQuerySafe } from "@/features/profile/api/selectProfile";
import { supabaseClient } from "@/lib/supabase";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { ui } from "@patient-watch/ui";
import { toast } from "@patient-watch/ui/sonner";
import { Check, Link2, LogIn } from "lucide-react";
import { useState } from "react";
import { buildImpersonateBrowserLink } from "@/features/admin/utils/impersonateLink";
import {
    friendlyAdminImpersonateMessage,
    readEdgeFunctionErrorMessage,
} from "@/features/admin/utils/edgeFunctionError";

export const Route = createFileRoute("/_app/users/create-user/impersonate")({
    validateSearch: validateCreateUserWizardSearch,
    beforeLoad: ({ search }) => {
        if (!search.userId) {
            throw redirect({ to: "/users/create-user/details" });
        }
    },
    component: CreateUserImpersonateStep,
});

interface MagicLinkResponse {
    properties?: { action_link?: string; hashed_token?: string };
    error?: string;
}

async function generateImpersonationLink(userEmail: string, adminEmail: string): Promise<string> {
    const redirectTo = typeof window !== "undefined" ? window.location.origin : undefined;
    const { data, error } = await supabaseClient.functions.invoke<MagicLinkResponse>("admin-impersonate", {
        body: { user_email: userEmail, admin_email: adminEmail, redirect_to: redirectTo },
    });

    // Legacy allowlist failure returned an empty array with status 400.
    if (Array.isArray(data)) {
        throw new Error("You are not authorised to impersonate users");
    }

    if (error || data?.error || (!data?.properties?.hashed_token && !data?.properties?.action_link)) {
        const raw = await readEdgeFunctionErrorMessage(
            error,
            data,
            "Could not generate a magic link. Please try again."
        );
        throw new Error(friendlyAdminImpersonateMessage(raw));
    }

    return buildImpersonateBrowserLink(data.properties);
}

function CreateUserImpersonateStep() {
    const search = Route.useSearch();
    const userId = search.userId!;
    const { data: adminProfile } = useProfileQuery();
    const { data: profile } = useProfileByIdQuerySafe(userId);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isOpening, setIsOpening] = useState(false);
    const [actionLink, setActionLink] = useState<string | null>(null);

    const displayName =
        [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") || profile?.email || "this user";
    const userEmail = profile?.email ?? "";
    const adminEmail = adminProfile?.email ?? "";

    const handleGenerateLink = async () => {
        if (!userEmail || !adminEmail) {
            toast.error("Missing user or admin email");
            return;
        }

        setIsGenerating(true);
        setActionLink(null);
        try {
            const link = await generateImpersonationLink(userEmail, adminEmail);
            setActionLink(link);
            try {
                await navigator.clipboard.writeText(link);
                toast.success("Magic link generated and copied");
            } catch {
                toast.success("Magic link generated");
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : "Could not generate magic link";
            toast.error(message);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleOpenAsUser = () => {
        if (!actionLink) return;

        setIsOpening(true);
        try {
            void navigator.clipboard.writeText(actionLink).catch(() => undefined);
            // Navigate to /magic?token_hash=... so verifyOtp replaces the admin session.
            // Do not call logout() first - that races with SIGNED_OUT → /login.
            window.location.assign(actionLink);
        } catch (err) {
            const message = err instanceof Error ? err.message : "Could not sign in as user";
            toast.error(message);
            setIsOpening(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="create-user-impersonate-step">
            <Stepper steps={getCreateUserWizardSteps("impersonate", search)} showNavigationLinks={false} />
            <div className="space-y-2">
                <h2 className="font-medium text-lg">Sign in as {displayName}</h2>
                <p className="text-muted-foreground text-sm">
                    Generate the magic link while you are still signed in as admin. Then open it here, or paste it in a
                    private window to keep this admin session. Or finish without impersonating.
                </p>
            </div>

            <ui.Alert>
                <ui.AlertTitle>{userEmail || "Email loading..."}</ui.AlertTitle>
                <ui.AlertDescription>
                    Step 1 needs your admin session. Step 2 signs you in as {displayName} in this tab. Prefer a private
                    window if you need to keep admin open.
                </ui.AlertDescription>
            </ui.Alert>

            <div className="flex flex-col gap-3">
                <ui.Button
                    type="button"
                    size="lg"
                    variant={actionLink ? "outline" : "default"}
                    className="w-full sm:w-auto"
                    disabled={isGenerating || !userEmail || !adminEmail}
                    onClick={() => void handleGenerateLink()}
                    data-testid="create-user-generate-magic-link"
                >
                    <Link2 className="mr-1.5 w-4 h-4" />
                    {isGenerating
                        ? "Generating magic link..."
                        : actionLink
                          ? "Regenerate magic link"
                          : "1. Generate magic link"}
                </ui.Button>

                {actionLink ? (
                    <div className="space-y-3" data-testid="create-user-magic-link-ready">
                        <Copy id={actionLink} value={actionLink}>
                            Magic link ready
                        </Copy>
                        <p className="text-muted-foreground text-sm">
                            Copied. Paste in a private window to keep admin, or open here:
                        </p>
                        <ui.Button
                            type="button"
                            size="lg"
                            className="w-full sm:w-auto"
                            disabled={isOpening}
                            onClick={handleOpenAsUser}
                            data-testid="create-user-sign-out-open"
                        >
                            <LogIn className="mr-1.5 w-4 h-4" />
                            {isOpening ? "Opening as user..." : "2. Sign out & open as user"}
                        </ui.Button>
                    </div>
                ) : null}

                <ui.Button asChild variant="outline" size="lg" className="w-full sm:w-auto" type="button">
                    <Link to="/users" data-testid="create-user-done">
                        <Check className="mr-1.5 w-4 h-4" />
                        Done - back to users
                    </Link>
                </ui.Button>
            </div>
        </div>
    );
}
